import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AnalystBenchProvider,
  useAnalystBench,
  findInstitutionById,
} from '../../context/AnalystBenchContext';
import './ReportDrafter.css';

// Report section configuration
interface ReportSection {
  id: string;
  title: string;
  description: string;
  defaultEnabled: boolean;
}

const REPORT_SECTIONS: ReportSection[] = [
  {
    id: 'executive-summary',
    title: 'Executive Summary',
    description: 'High-level overview of the credit assessment and key findings',
    defaultEnabled: true,
  },
  {
    id: 'company-profile',
    title: 'Company Profile',
    description: 'Institution background, history, and organizational structure',
    defaultEnabled: true,
  },
  {
    id: 'financial-analysis',
    title: 'Financial Analysis',
    description: 'Balance sheet review and key financial metrics',
    defaultEnabled: true,
  },
  {
    id: 'quantitative-assessment',
    title: 'Quantitative Assessment',
    description: 'CAMEL component scores and quantitative rating methodology',
    defaultEnabled: true,
  },
  {
    id: 'qualitative-assessment',
    title: 'Qualitative Assessment',
    description: 'Management quality, governance, and market position analysis',
    defaultEnabled: true,
  },
  {
    id: 'rating-rationale',
    title: 'Rating Rationale',
    description: 'Detailed explanation of the final credit rating determination',
    defaultEnabled: true,
  },
  {
    id: 'risk-factors',
    title: 'Risk Factors',
    description: 'Key risks and mitigating factors',
    defaultEnabled: true,
  },
  {
    id: 'outlook',
    title: 'Outlook & Recommendations',
    description: 'Future outlook and strategic recommendations',
    defaultEnabled: true,
  },
];

function ReportDrafterContent() {
  const navigate = useNavigate();
  const { institutionId, period } = useParams<{ institutionId: string; period: string }>();
  const { selectedInstitution, selectedPeriod, setActiveSection } = useAnalystBench();

  // Find institution from params if not in context
  const institution = selectedInstitution || findInstitutionById(institutionId || '');
  const reportPeriod = selectedPeriod || period;

  const [enabledSections, setEnabledSections] = useState<Set<string>>(
    new Set(REPORT_SECTIONS.filter(s => s.defaultEnabled).map(s => s.id))
  );
  const [activeSectionId, setActiveSectionId] = useState<string>('executive-summary');
  const [sectionContents, setSectionContents] = useState<Record<string, string>>({});
  const [isGenerating, setIsGenerating] = useState(false);

  const handleBack = () => {
    if (institution && reportPeriod) {
      setActiveSection('landing');
      navigate(`/analyst-bench/${institution.id}/${reportPeriod}`);
    } else {
      navigate('/analyst-bench');
    }
  };

  const toggleSection = (sectionId: string) => {
    setEnabledSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const handleSectionContentChange = (sectionId: string, content: string) => {
    setSectionContents(prev => ({
      ...prev,
      [sectionId]: content,
    }));
  };

  const handleGenerateWithAI = async (sectionId: string) => {
    setIsGenerating(true);
    // Simulate AI generation
    await new Promise(resolve => setTimeout(resolve, 1500));
    const mockContent = `This is AI-generated content for the ${REPORT_SECTIONS.find(s => s.id === sectionId)?.title} section.\n\nBased on the analysis of ${institution?.name} for the period ${reportPeriod}, the following observations were made:\n\n1. Key finding one related to this section\n2. Key finding two with supporting data\n3. Recommendations based on analysis\n\nThis content can be edited as needed.`;
    handleSectionContentChange(sectionId, mockContent);
    setIsGenerating(false);
  };

  const handleExportPDF = () => {
    alert('PDF export functionality - integrates with existing report generation');
  };

  const formatPeriod = (p: string) => {
    const [year, month] = p.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const activeSection = REPORT_SECTIONS.find(s => s.id === activeSectionId);

  return (
    <div className="report-drafter">
      {/* Header */}
      <header className="drafter-header">
        <button className="back-button" onClick={handleBack}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          <span>Back to Workbench</span>
        </button>
        <div className="header-info">
          <h1>Report Drafter</h1>
          <span className="header-meta">
            {institution?.name} - {reportPeriod && formatPeriod(reportPeriod)}
          </span>
        </div>
        <div className="header-actions">
          <button className="btn-secondary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
              <polyline points="17 21 17 13 7 13 7 21"/>
              <polyline points="7 3 7 8 15 8"/>
            </svg>
            Save Draft
          </button>
          <button className="btn-primary" onClick={handleExportPDF}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="12" y1="18" x2="12" y2="12"/>
              <line x1="9" y1="15" x2="15" y2="15"/>
            </svg>
            Export PDF
          </button>
        </div>
      </header>

      <div className="drafter-content">
        {/* Left: Section List */}
        <aside className="section-list">
          <div className="section-list-header">
            <h2>Report Sections</h2>
            <span className="section-count">{enabledSections.size} of {REPORT_SECTIONS.length} enabled</span>
          </div>
          <div className="sections">
            {REPORT_SECTIONS.map(section => (
              <div
                key={section.id}
                className={`section-item ${activeSectionId === section.id ? 'active' : ''} ${!enabledSections.has(section.id) ? 'disabled' : ''}`}
              >
                <label className="section-toggle">
                  <input
                    type="checkbox"
                    checked={enabledSections.has(section.id)}
                    onChange={() => toggleSection(section.id)}
                  />
                  <span className="toggle-slider" />
                </label>
                <button
                  className="section-info"
                  onClick={() => setActiveSectionId(section.id)}
                  disabled={!enabledSections.has(section.id)}
                >
                  <span className="section-title">{section.title}</span>
                  {sectionContents[section.id] && (
                    <span className="section-status">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </span>
                  )}
                </button>
              </div>
            ))}
          </div>
        </aside>

        {/* Right: Editor */}
        <main className="section-editor">
          {activeSection ? (
            <>
              <div className="editor-header">
                <div className="editor-title">
                  <h2>{activeSection.title}</h2>
                  <p>{activeSection.description}</p>
                </div>
                <button
                  className="btn-ai"
                  onClick={() => handleGenerateWithAI(activeSection.id)}
                  disabled={isGenerating || !enabledSections.has(activeSection.id)}
                >
                  {isGenerating ? (
                    <>
                      <svg className="spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                      </svg>
                      Generating...
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                        <path d="M2 17l10 5 10-5"/>
                        <path d="M2 12l10 5 10-5"/>
                      </svg>
                      Generate with AI
                    </>
                  )}
                </button>
              </div>
              <div className="editor-content">
                <textarea
                  className="section-textarea"
                  placeholder={`Write content for ${activeSection.title}...\n\nYou can also click "Generate with AI" to auto-generate content based on the institution data.`}
                  value={sectionContents[activeSection.id] || ''}
                  onChange={(e) => handleSectionContentChange(activeSection.id, e.target.value)}
                  disabled={!enabledSections.has(activeSection.id)}
                />
              </div>
              <div className="editor-footer">
                <span className="char-count">
                  {(sectionContents[activeSection.id] || '').length} characters
                </span>
                <div className="editor-actions">
                  <button
                    className="btn-text"
                    onClick={() => handleSectionContentChange(activeSection.id, '')}
                    disabled={!sectionContents[activeSection.id]}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="editor-empty">
              <p>Select a section from the list to start editing</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default function ReportDrafter() {
  return (
    <AnalystBenchProvider>
      <ReportDrafterContent />
    </AnalystBenchProvider>
  );
}
